import type { Product } from '@domain/product'
import { Miniatura } from './Miniatura'

/**
 * As fotos de um grupo, abertas como um baralho.
 *
 * ## O que ela responde de relance
 *
 * Numa lista de grupos, "Coca-Cola" é só uma palavra. O baralho mostra **como
 * as embalagens são** — que é como a pessoa reconhece o grupo antes de ler.
 *
 * ## Baralho, e não fila de círculos (decisão 226)
 *
 * A versão anterior eram três círculos de 32 px sobrepostos. Círculo corta o
 * produto: uma garrafa é alta e estreita, e o recorte redondo joga fora a
 * silhueta, que é justamente o que se reconhece de longe. Carta retangular com
 * leve rotação guarda a forma e ainda diz "há mais de um aqui" sem contar.
 *
 * ## A carta é medida em % da coluna, e não em pixels
 *
 * A primeira versão usava `h-16 w-12` fixo dentro de uma coluna de `w-1/3`.
 * Num celular passava; numa tela larga a coluna cresce, a carta não, e a foto
 * fica perdida num terço vazio. Medir a carta como fração da coluna faz o
 * baralho ocupar o espaço que lhe foi dado, em qualquer largura.
 *
 * A largura depende de **quantas** cartas há: com três elas se sobrepõem para
 * caber, com uma ela pode ser larga. Uma foto só, pequena no meio de um terço
 * vazio, é o pior dos dois mundos.
 *
 * ## Três, e o resto vira número
 *
 * Abrir dez cartas dá uma faixa ilegível. Três é o que cabe no terço da linha
 * com sobreposição sem virar borrão, e o "+4" diz o que ficou de fora — some,
 * não mente.
 *
 * ## Altura fixa, porque senão a lista fica dentada
 *
 * A carta é medida em largura, e a largura depende de quantas cartas há — logo
 * a ALTURA também dependia. Medido: linha de uma foto com 140 px ao lado de
 * linha de três com 95. Numa lista, isso lê como desalinho, não como variedade.
 * O teto de `6rem` com `object-cover` corta o excedente em vez de deixar a
 * linha crescer.
 *
 * ## Quem decide a LARGURA da coluna é quem chama
 *
 * O componente ocupa `w-full` e mede as cartas em % disso. Ele já teve o
 * `w-1/3` embutido, e o resultado foi um terço **de um terço**: a lista de
 * grupos passou a envolvê-lo num link que também era `w-1/3`, e a carta desabou
 * para 22 px sem nada reclamar. Largura de coluna é decisão do layout que
 * hospeda, não do desenho que preenche.
 *
 * ## Sem foto, o lugar CONTINUA reservado
 *
 * Ao contrário da versão anterior, que devolvia `null`. O motivo mudou junto
 * com o desenho: a coluna agora vale um terço da linha, e um grupo sem foto
 * faria o nome dele começar num lugar e o do vizinho em outro — a lista inteira
 * serrilhada. É a mesma conclusão da linha de embalagem na decisão 225: quando
 * a foto é estrutura, a ausência dela precisa de lugar.
 */

const VISIVEIS = 3
/** Graus por carta. Pouco: baralho aberto, não leque de mágico. */
const INCLINACAO = 7

/**
 * Largura da carta e sobreposição, em % da coluna, por número de cartas.
 *
 * O vão nominal é `largura + (n-1) * (largura - sobrepor)`, e ele fecha bem
 * abaixo de 100% de propósito: **a rotação alarga a carta**. Uma carta girada
 * 7° ocupa `w·cos7 + h·sin7` de largura real — com `aspect-3/4`, isso são ~16%
 * a mais do que a largura declarada. Medido no navegador: a primeira tabela,
 * que fechava em 96% no papel, transbordava a coluna em 12 px.
 *
 * Só as cartas das PONTAS giram, então a folga precisa cobrir duas delas.
 */
const MEDIDAS: Record<number, { largura: number; sobrepor: number }> = {
  1: { largura: 80, sobrepor: 0 },
  2: { largura: 56, sobrepor: 22 },
  3: { largura: 46, sobrepor: 26 },
}

export function PilhaDeFotos({
  variantes,
  nome,
  altura = 'h-24',
}: {
  variantes: Product[]
  nome: string
  /**
   * A altura do baralho, em classe do Tailwind.
   *
   * **Fixa, e não derivada da largura** — é o conserto da decisão 229(e): a
   * carta é medida em % da coluna e a largura depende de quantas cartas há,
   * então a altura dependeria também, e a lista ficava dentada (uma linha com
   * 140px ao lado de outra com 95). Quem escolhe é o contexto: uma linha de
   * lista de grupos aguenta 6rem, uma linha de seletor não.
   */
  altura?: string
}) {
  const comFoto = variantes.filter((p) => p.imageUrl !== null)
  const mostradas = comFoto.slice(0, VISIVEIS)
  const restantes = comFoto.length - mostradas.length

  const { largura, sobrepor } = MEDIDAS[Math.min(mostradas.length, VISIVEIS)] ?? MEDIDAS[3]!

  return (
    <span className={`flex w-full items-center justify-center py-1 ${altura}`}>
      {mostradas.length === 0 ? (
        // A largura vem daqui, e não do componente — decisão 229(d).
        <Miniatura nome={nome} src={null} className="aspect-3/4 max-h-full w-[62%]" />
      ) : (
        mostradas.map((p, indice) => (
          <img
            key={p.id}
            src={p.imageUrl ?? ''}
            // Vazio de propósito: o baralho é reconhecimento de uma linha que
            // já diz o nome do grupo. Um `alt` por foto faria o leitor de tela
            // recitar três nomes de embalagem antes do que importa.
            alt=""
            className="aspect-3/4 max-h-full rounded-lg border-2 border-[var(--bg)] bg-[var(--surface)] object-cover shadow-md"
            style={{
              // **Uma carta sozinha é medida pela ALTURA.** As larguras em %
              // da tabela existem para as cartas se sobreporem, e uma só não
              // tem com quem se sobrepor. Com % ela ficava mais larga do que
              // o `aspect-3/4` pede e o `max-h-full` cortava a altura: uma
              // garrafa virava um quadrado. Medido no seletor de família, onde
              // a altura é 44px — 80% de 74px dá 59px de largura para 44 de
              // altura, quando a proporção pede 33.
              width: mostradas.length === 1 ? 'auto' : `${String(largura)}%`,
              height: mostradas.length === 1 ? '100%' : undefined,
              // A carta do meio fica reta; as das pontas inclinam para fora.
              transform: `rotate(${String((indice - (mostradas.length - 1) / 2) * INCLINACAO)}deg)`,
              marginLeft: indice === 0 ? 0 : `-${String(sobrepor)}%`,
              zIndex: indice,
            }}
          />
        ))
      )}
      {restantes > 0 && (
        <span className="z-10 -ml-[6%] grid aspect-1/2 max-h-full w-[22%] place-items-center rounded-lg border-2 border-[var(--bg)] bg-[var(--border)] text-[10px] tabular-nums">
          {`+${String(restantes)}`}
        </span>
      )}
    </span>
  )
}
